import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { 
  Euro, 
  Package, 
  Plus,
  Edit,
  Trash2,
  Save,
  Clock,
  TrendingDown,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

const AdminPricingPage = () => {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPricing();
  }, []);

  const loadPricing = async () => {
    try {
      setLoading(true);
      const res = await api.get('/settings/pricing');
      setPricing(res.data || {
        default_hourly_rate: 75,
        packs: []
      });
    } catch (error) {
      console.error('Error loading pricing:', error);
      setPricing({
        default_hourly_rate: 75,
        packs: [
          { id: '1', name: 'Pack Starter', hours: 5, price: 350, active: true },
          { id: '2', name: 'Pack Pro', hours: 10, price: 650, active: true },
          { id: '3', name: 'Pack Business', hours: 20, price: 1200, active: true },
          { id: '4', name: 'Pack Enterprise', hours: 50, price: 2750, active: true },
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateDiscount = (hours, price) => {
    if (!pricing?.default_hourly_rate || !hours || !price) return 0;
    const normalPrice = hours * pricing.default_hourly_rate;
    return ((normalPrice - price) / normalPrice * 100).toFixed(1);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/pricing', pricing);
      toast.success('Tarification mise à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updatePack = (packIndex, field, value) => {
    const newPacks = [...(pricing.packs || [])];
    newPacks[packIndex] = {
      ...newPacks[packIndex],
      [field]: value
    };
    setPricing({ ...pricing, packs: newPacks });
  };

  const addPack = () => {
    const newPacks = [...(pricing.packs || [])];
    newPacks.push({
      id: Date.now().toString(),
      name: 'Nouveau Pack',
      hours: 10,
      price: pricing.default_hourly_rate * 10 * 0.9, // 10% discount
      active: true
    });
    setPricing({ ...pricing, packs: newPacks });
  };

  const removePack = (index) => {
    if (!window.confirm('Supprimer ce pack ?')) return;
    const newPacks = pricing.packs.filter((_, i) => i !== index);
    setPricing({ ...pricing, packs: newPacks });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a365d]">Tarification & Packs</h1>
          <p className="text-[#64748b]">Gérez vos tarifs horaires et packs d'heures</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>

      {/* Base Rate */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-600" />
            Taux horaire de base
          </CardTitle>
          <CardDescription>Ce taux est utilisé pour calculer les remises des packs et facturer les heures supplémentaires</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1 max-w-xs">
              <Label>Prix de l'heure (€)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input 
                  type="number"
                  value={pricing?.default_hourly_rate || 75}
                  onChange={(e) => setPricing({...pricing, default_hourly_rate: parseFloat(e.target.value) || 75})}
                  className="text-xl font-bold"
                />
                <span className="text-xl text-[#64748b]">€/h</span>
              </div>
            </div>
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Exemple :</strong> Un projet de 10h sera facturé {((pricing?.default_hourly_rate || 75) * 10).toLocaleString('fr-FR')}€ hors pack.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Packs */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-600" />
              Packs d'heures
            </CardTitle>
            <CardDescription>Les clients peuvent acheter ces packs pour recharger leur wallet</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={addPack}>
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un pack
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {(pricing?.packs || []).map((pack, index) => {
              const discount = calculateDiscount(pack.hours, pack.price);
              return (
                <div key={pack.id || index} className="flex items-center gap-4 p-4 rounded-lg border bg-white">
                  <div className="flex-1">
                    <Input 
                      value={pack.name}
                      onChange={(e) => updatePack(index, 'name', e.target.value)}
                      className="font-semibold text-[#1a365d] border-0 p-0 h-auto text-base focus-visible:ring-0"
                    />
                  </div>
                  <div className="w-24">
                    <Label className="text-xs text-[#64748b]">Heures</Label>
                    <Input 
                      type="number"
                      value={pack.hours}
                      onChange={(e) => updatePack(index, 'hours', parseInt(e.target.value) || 0)}
                      className="h-8"
                    />
                  </div>
                  <div className="w-28">
                    <Label className="text-xs text-[#64748b]">Prix (€)</Label>
                    <Input 
                      type="number"
                      value={pack.price}
                      onChange={(e) => updatePack(index, 'price', parseFloat(e.target.value) || 0)}
                      className="h-8"
                    />
                  </div>
                  <div className="w-24 text-center">
                    <Label className="text-xs text-[#64748b]">Remise</Label>
                    <Badge 
                      variant="secondary" 
                      className={`mt-1 ${parseFloat(discount) > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}
                    >
                      <TrendingDown className="w-3 h-3 mr-1" />
                      {discount}%
                    </Badge>
                  </div>
                  <div className="w-20 text-center">
                    <Label className="text-xs text-[#64748b]">€/h</Label>
                    <p className="text-sm font-medium mt-1">
                      {pack.hours > 0 ? (pack.price / pack.hours).toFixed(0) : 0}€
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={pack.active !== false}
                      onCheckedChange={(checked) => updatePack(index, 'active', checked)}
                    />
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-red-500 hover:text-red-700"
                      onClick={() => removePack(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {(!pricing?.packs || pricing.packs.length === 0) && (
            <div className="text-center py-8 text-slate-500">
              <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Aucun pack créé</p>
              <Button onClick={addPack} variant="outline" className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Créer un pack
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="border-amber-200 bg-amber-50/50">
        <CardContent className="p-4">
          <p className="text-sm text-amber-800">
            <strong>💡 Info :</strong> Les clients voient ces packs lors du rechargement de leur wallet. Les prix incluent les remises affichées. Les packs inactifs ne sont pas proposés aux clients.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPricingPage;
